import { ErrorBoundary, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Box, Center, FlatList } from 'native-base';
import React, { useCallback } from 'react';
import { Dimensions } from 'react-native';

import { WithProductId } from '../..';
import { BarSelectedItems } from '../../../BarSelectedItems';
import { ProductFieldProps } from '../../constants';

import { ProductMobileCard } from './components/Card';
import { ProductMobileCardLoading } from './components/CardLoading';

export interface ProductTableMobileProps<T extends WithProductId> {
  data: T[];
  isLoading: boolean;
  fields: ProductFieldProps<T>[];
  columns: number;
  rows?: number;
  selectedItems: string[];
  countSelected: number;
  onSelect: (value: string) => void;
  onDetails: (productId: number, managerImage: string, productName: string) => void;
  onBatchAction: (action: 'block' | 'unblock') => void;
  handleSelecteItems: (value: string) => void;
  isSelectAll: boolean;
  productType: 'funds' | 'fixedIncome';
}

export const ProductTableMobile = <T extends WithProductId>({
  data,
  fields = [],
  isLoading,
  columns,
  rows = 1,
  selectedItems,
  countSelected,
  onSelect,
  onDetails,
  onBatchAction,
  handleSelecteItems,
  isSelectAll,
  productType,
}: ProductTableMobileProps<T>) => {
  const maxHeight = Dimensions.get('window').height - Dimensions.get('window').height * 0.35;

  const renderProductItem = useCallback(
    (item: T) => {
      return (
        <ProductMobileCard
          key={item.productId.toString()}
          fields={fields}
          item={item}
          onSelect={onSelect}
          selectedItems={selectedItems}
          onDetails={onDetails}
          productType={productType}
        />
      );
    },
    [fields, rows, columns, isLoading, selectedItems, onSelect]
  );

  if (isLoading) {
    return Array.from({ length: 5 }).map((_, index) => (
      <ProductMobileCardLoading key={index} fieldsAmount={fields.length} />
    ));
  }

  if (data?.length === 0 && !isLoading) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary
          errorIconOrIllustration="NotFoundIllustration"
          errorTitle="Nenhum resultado corresponde à sua pesquisa"
          errorMessage="Por favor, verifique o campo informado e tente novamente."
        />
      </Center>
    );
  }

  return (
    <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']}>
      {countSelected > 1 && (
        <BarSelectedItems
          countSelected={countSelected}
          onBatchAction={onBatchAction}
          handleSelecteItems={handleSelecteItems}
          isSelectAll={isSelectAll}
        />
      )}
      <FlatList
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        removeClippedSubviews={true}
        data={data}
        contentContainerStyle={{ maxHeight, padding: parseInt(Spacing['stack.nano']) }}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.5}
        ItemSeparatorComponent={() => <Box height={Spacing['stack.xxs']} />}
        renderItem={({ item }) => renderProductItem(item)}
        keyExtractor={(item, index) => `${item.productId}-${index}`}
        getItemLayout={(_, index) => ({
          length: 150,
          offset: 150 * index,
          index,
        })}
      />
    </VStack>
  );
};
